import { useEffect, useRef, useState } from 'react';

// Six big boxes for the pairing code.
//
// Typed once, on an iPad, by someone reading the code off a laptop across the
// room — so the boxes are large, the caret moves on its own, and a pasted code
// fills the lot. Ambiguous characters never appear in a code (no O/0, no
// I/1/L), so there is nothing to squint at.

const LENGTH = 6;

export default function PairingCodeInput({ onComplete, disabled, error }) {
  const [chars, setChars] = useState(Array(LENGTH).fill(''));
  const inputs = useRef([]);

  useEffect(() => { inputs.current[0]?.focus(); }, []);

  const emit = (next) => {
    setChars(next);
    const code = next.join('');
    if (code.length === LENGTH && !next.includes('')) onComplete(code);
  };

  const setAt = (index, value) => {
    // Only ever one character per box, uppercased as it is typed.
    const char = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(-1);
    const next = [...chars];
    next[index] = char;
    emit(next);
    if (char && index < LENGTH - 1) inputs.current[index + 1]?.focus();
  };

  const onKeyDown = (index, event) => {
    if (event.key === 'Backspace') {
      event.preventDefault();
      const next = [...chars];
      // Backspace in an empty box steps back and clears the previous one,
      // which is what people expect from a code field.
      if (next[index]) next[index] = '';
      else if (index > 0) {
        next[index - 1] = '';
        inputs.current[index - 1]?.focus();
      }
      setChars(next);
      return;
    }
    if (event.key === 'ArrowLeft' && index > 0) inputs.current[index - 1]?.focus();
    if (event.key === 'ArrowRight' && index < LENGTH - 1) inputs.current[index + 1]?.focus();
  };

  const onPaste = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData('text')
      .toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, LENGTH).split('');
    if (pasted.length === 0) return;

    const next = Array(LENGTH).fill('');
    pasted.forEach((char, i) => { next[i] = char; });
    emit(next);
    inputs.current[Math.min(pasted.length, LENGTH - 1)]?.focus();
  };

  return (
    <div>
      <div className="flex justify-center gap-2 sm:gap-3">
        {chars.map((char, index) => (
          <input
            key={index}
            ref={(el) => { inputs.current[index] = el; }}
            value={char}
            onChange={(e) => setAt(index, e.target.value)}
            onKeyDown={(e) => onKeyDown(index, e)}
            onPaste={onPaste}
            onFocus={(e) => e.target.select()}
            disabled={disabled}
            inputMode="text"
            autoCapitalize="characters"
            autoComplete="one-time-code"
            aria-label={`Character ${index + 1} of ${LENGTH}`}
            className={`h-20 w-14 rounded-2xl border-2 bg-[#2e1c2a] text-center text-3xl font-bold text-[#f7f1e6] outline-none transition-colors sm:h-24 sm:w-16 sm:text-4xl ${
              error
                ? 'border-[#f58baa]'
                : char
                  ? 'border-[#ee8a1e]'
                  : 'border-[#443040] focus:border-[#ee8a1e]'
            } disabled:opacity-50`}
          />
        ))}
      </div>
      {error && <p className="mt-4 text-center text-[#f58baa]">{error}</p>}
    </div>
  );
}
